import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { AuthService, PendingTwoFactorChallenge, User } from '../../core/auth/auth.service';
import { ThemeService } from '../../shared/services/theme.service';
import { AppSettings, AppSettingsService } from '../../shared/services/app-settings.service';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.css']
})
export class OtpComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly otpDigitIndexes = [0, 1, 2, 3];
  digits: string[] = ['', '', '', ''];
  otpForm: FormGroup;
  showOtpError = false;
  loading = false;
  resendingOtp = false;
  resendCountdownSeconds = 300;
  challenge: PendingTwoFactorChallenge | null = null;
  isDarkMode$ = this.themeService.isDarkMode$;
  settings$: Observable<AppSettings | null> = this.appSettingsService.settings$;
  isBrandingLoading = true;

  private userSubscription: Subscription | null = null;
  private currentUser: User | null = null;
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private otpErrorTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly resendIntervalSeconds = 300;
  private readonly otpErrorDelayMs = 2500;

  @ViewChildren('otpDigitInput') otpDigitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private analyticsService: AnalyticsService,
    private themeService: ThemeService,
    private appSettingsService: AppSettingsService
  ) {
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    });
  }

  ngAfterViewInit(): void {
    this.focusDigitInput(0);
  }

  ngOnInit(): void {
    void this.hydrateBrandingForCurrentUser();
    this.userSubscription = this.authService.getCurrentUser().subscribe((user) => {
      this.currentUser = user;
      if (!user) {
        void this.router.navigate(['/login']);
      }
    });
    void this.ensureChallenge();
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.clearResendTimer();
    this.clearOtpErrorTimer();
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  get isOtpComplete(): boolean {
    return /^\d{4}$/.test(this.digits.join(''));
  }

  onDigitInput(index: number, event: Event): void {
    this.scheduleOtpErrorCheck();
    const input = event.target as HTMLInputElement;
    const numericValue = input.value.replace(/\D/g, '');

    if (numericValue.length > 1) {
      this.fillDigits(numericValue);
      input.value = this.digits[index];
      return;
    }

    this.digits[index] = numericValue.slice(0, 1);
    input.value = this.digits[index];
    this.syncOtpFormValue();

    if (this.digits[index] && index < this.digits.length - 1) {
      this.focusDigitInput(index + 1);
    }
  }

  onDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key !== 'Backspace') {
      return;
    }

    if (this.digits[index]) {
      return;
    }

    if (index === 0) {
      return;
    }

    event.preventDefault();
    const previousIndex = index - 1;
    this.digits[previousIndex] = '';
    this.syncOtpFormValue();
    const previousInput = this.otpDigitInputs.get(previousIndex)?.nativeElement;
    if (previousInput) {
      previousInput.value = '';
    }
    this.focusDigitInput(previousIndex);
    this.scheduleOtpErrorCheck();
  }

  onDigitPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedValue = event.clipboardData?.getData('text') ?? '';
    this.fillDigits(pastedValue);
    this.scheduleOtpErrorCheck();
  }

  async onSubmit(): Promise<void> {
    if (!this.isOtpComplete || !this.challenge) {
      this.showOtpError = true;
      this.clearOtpErrorTimer();
      return;
    }
    this.showOtpError = false;
    this.clearOtpErrorTimer();
    this.loading = true;
    try {
      const response = await firstValueFrom(this.apiService.verifyTwoFactorChallenge({
        challengeId: this.challenge.challengeId,
        otp: this.digits.join(''),
      }));
      this.authService.setTwoFactorSessionToken(response.sessionToken);
      this.authService.clearPendingTwoFactorChallenge();

      const user = await firstValueFrom(this.authService.getCurrentUser().pipe(take(1)));
      this.analyticsService.trackLoginSucceeded(user?.role);
      await this.navigateByRole(user);
    } catch (error: any) {
      this.analyticsService.trackLoginFailed(error?.error?.error?.code || error?.message || 'otp_verify_failed');
      const message = error?.error?.error?.message || 'OTP verification failed.';
      this.snackBar.open(message, 'Close', { duration: 5000 });
      this.clearDigits();
    } finally {
      this.loading = false;
    }
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  get canResendOtp(): boolean {
    return this.resendCountdownSeconds === 0 && !this.loading && !this.resendingOtp;
  }

  get resendCountdownDisplay(): string {
    const minutes = Math.floor(this.resendCountdownSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (this.resendCountdownSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  async resendOtp(): Promise<void> {
    if (!this.challenge || !this.canResendOtp) {
      return;
    }

    this.resendingOtp = true;
    try {
      const response = await firstValueFrom(this.apiService.resendTwoFactorChallenge());
      this.challenge = {
        challengeId: response.challengeId,
        expiresAt: response.expiresAt,
        resendAvailableAt: response.resendAvailableAt,
      };
      this.authService.setPendingTwoFactorChallenge(this.challenge);
      this.startResendCountdown(this.getMinimumResendSeconds(response.resendAvailableAt));
      this.clearDigits();
      this.snackBar.open(response.message || 'A new OTP has been sent to your email.', 'Close', { duration: 5000 });
    } catch (error: any) {
      const message = error?.error?.error?.message || 'Could not resend OTP.';
      this.snackBar.open(message, 'Close', { duration: 5000 });
    } finally {
      this.resendingOtp = false;
    }
  }

  private async ensureChallenge(): Promise<void> {
    this.challenge = this.authService.getPendingTwoFactorChallenge();
    if (!this.challenge) {
      try {
        const response = await firstValueFrom(this.apiService.startTwoFactorChallenge());
        if (!response.required || !response.challengeId || !response.expiresAt || !response.resendAvailableAt) {
          await this.navigateByRole(this.currentUser);
          return;
        }
        this.challenge = {
          challengeId: response.challengeId,
          expiresAt: response.expiresAt,
          resendAvailableAt: response.resendAvailableAt,
        };
        this.authService.setPendingTwoFactorChallenge(this.challenge);
        this.startResendCountdown(this.getMinimumResendSeconds(response.resendAvailableAt));
      } catch (error: any) {
        const message = error?.error?.error?.message || 'Could not start two-factor verification.';
        this.snackBar.open(message, 'Close', { duration: 5000 });
        await this.authService.logout();
        return;
      }
      return;
    }

    this.startResendCountdown(this.getMinimumResendSeconds(this.challenge.resendAvailableAt));
  }

  private async navigateByRole(user: User | null): Promise<void> {
    const billingNotice = await this.resolvePostLoginBillingNotice(user);
    if (await this.shouldRouteToTeamBillingSetup(user)) {
      await this.router.navigate(['/billing/plan-selector'], { queryParams: { scope: 'team' } });
      return;
    }
    if (await this.shouldRouteToPlanSelector(user)) {
      await this.router.navigate(['/billing/plan-selector']);
      return;
    }
    if (user?.role === 'admin') {
      await this.router.navigate(['/admin/new-request'], {
        queryParams: billingNotice ? { billingNotice } : undefined,
      });
      return;
    }
    if (user?.role === 'user') {
      await this.router.navigate(['/user/data-request'], {
        queryParams: billingNotice ? { billingNotice } : undefined,
      });
      return;
    }
    await this.router.navigate(['/admin/new-request'], {
      queryParams: billingNotice ? { billingNotice } : undefined,
    });
  }

  private async resolvePostLoginBillingNotice(user: User | null): Promise<'payment-required' | null> {
    if (user?.role !== 'user') {
      return null;
    }

    try {
      const profile = await firstValueFrom(this.apiService.getCurrentUserProfile());
      const profileUser = profile.user;
      if (
        profileUser.team === true ||
        profileUser.approvalStatus !== 'APPROVED' ||
        !profileUser.selectedPlanId ||
        profileUser.selectedPlanCode === 'INDIVIDUAL_TRIAL'
      ) {
        return null;
      }

      const billing = await firstValueFrom(this.apiService.getBillingProfile());
      const hasVerifiedPaymentMethod = billing?.paymentMethod?.verified === true;
      const hasPaystackSubscription = billing?.hasPaystackSubscription === true;
      const totalCredits = Number(billing?.credits?.totalRemaining || 0);
      if (!hasVerifiedPaymentMethod && !hasPaystackSubscription && totalCredits <= 0) {
        return 'payment-required';
      }
      return null;
    } catch {
      return null;
    }
  }

  private async hydrateBrandingForCurrentUser(): Promise<void> {
    this.isBrandingLoading = true;
    try {
      const response = await firstValueFrom(this.apiService.listMyOrganizations());
      const organizations = response.organizations || [];
      const selectedOrgId =
        String(response.activeOrgId || '').trim() ||
        String(this.apiService.getActiveOrgId() || '').trim() ||
        String(organizations[0]?.orgId || '').trim();
      const selectedOrg = organizations.find((org) => org.orgId === selectedOrgId) || organizations[0];
      if (selectedOrg) {
        this.appSettingsService.applyOrganizationBranding({
          orgId: selectedOrg.orgId,
          displayName: selectedOrg.displayName,
          logoUrl: selectedOrg.logoUrl || null,
          primaryColor: selectedOrg.primaryColor || null,
          secondaryColor: selectedOrg.secondaryColor || null,
        });
      }
    } catch {
      // Fall back to base/cached tenant settings when org context cannot be resolved yet.
    } finally {
      this.isBrandingLoading = false;
    }
  }

  private async shouldRouteToPlanSelector(user: User | null): Promise<boolean> {
    try {
      const profile = await firstValueFrom(this.apiService.getCurrentUserProfile());
      const profileUser = profile.user;
      if (user?.role !== 'user') {
        return false;
      }
      return (
        profileUser.enabled === true &&
        profileUser.team !== true &&
        profileUser.approvalStatus === 'APPROVED' &&
        !profileUser.selectedPlanId
      );
    } catch {
      return false;
    }
  }

  private async shouldRouteToTeamBillingSetup(user: User | null): Promise<boolean> {
    if (user?.role !== 'admin') {
      return false;
    }
    try {
      const profile = await firstValueFrom(this.apiService.getCurrentUserProfile());
      const profileUser = profile.user;
      return (
        profileUser.enabled === true &&
        profileUser.team === true &&
        profileUser.isBillingOwner === true &&
        profileUser.requiresTeamBillingSetup === true
      );
    } catch {
      return false;
    }
  }

  private getMinimumResendSeconds(resendAvailableAt?: string): number {
    const secondsUntilResend = this.getSecondsUntil(resendAvailableAt);
    if (secondsUntilResend === null) {
      return this.resendIntervalSeconds;
    }
    return Math.max(this.resendIntervalSeconds, secondsUntilResend);
  }

  private getSecondsUntil(dateIsoString?: string): number | null {
    if (!dateIsoString) {
      return null;
    }
    const parsedTime = new Date(dateIsoString).getTime();
    if (Number.isNaN(parsedTime)) {
      return null;
    }
    return Math.max(0, Math.ceil((parsedTime - Date.now()) / 1000));
  }

  private startResendCountdown(seconds: number): void {
    this.clearResendTimer();
    this.resendCountdownSeconds = Math.max(0, Math.floor(seconds));
    if (this.resendCountdownSeconds === 0) {
      return;
    }

    this.resendTimer = setInterval(() => {
      if (this.resendCountdownSeconds <= 1) {
        this.resendCountdownSeconds = 0;
        this.clearResendTimer();
        return;
      }
      this.resendCountdownSeconds -= 1;
    }, 1000);
  }

  private fillDigits(rawValue: string): void {
    const parsedDigits = rawValue.replace(/\D/g, '').slice(0, 4);
    for (let index = 0; index < this.digits.length; index += 1) {
      this.digits[index] = parsedDigits[index] ?? '';
      const input = this.otpDigitInputs?.get(index)?.nativeElement;
      if (input) {
        input.value = this.digits[index];
      }
    }
    this.syncOtpFormValue();

    if (parsedDigits.length === this.digits.length) {
      this.focusDigitInput(this.digits.length - 1);
      return;
    }

    this.focusDigitInput(Math.min(parsedDigits.length, this.digits.length - 1));
  }

  private scheduleOtpErrorCheck(): void {
    this.clearOtpErrorTimer();
    this.showOtpError = false;

    if (this.isOtpComplete) {
      return;
    }

    this.otpErrorTimer = setTimeout(() => {
      if (!this.isOtpComplete) {
        this.showOtpError = true;
      }
      this.otpErrorTimer = null;
    }, this.otpErrorDelayMs);
  }

  private clearOtpErrorTimer(): void {
    if (!this.otpErrorTimer) {
      return;
    }
    clearTimeout(this.otpErrorTimer);
    this.otpErrorTimer = null;
  }

  private clearDigits(): void {
    this.digits = ['', '', '', ''];
    this.showOtpError = false;
    this.clearOtpErrorTimer();
    this.syncOtpFormValue();
    this.otpDigitInputs?.forEach((inputRef) => {
      inputRef.nativeElement.value = '';
    });
    this.focusDigitInput(0);
  }

  private syncOtpFormValue(): void {
    this.otpForm.patchValue({ otp: this.digits.join('') }, { emitEvent: false });
  }

  private focusDigitInput(index: number): void {
    setTimeout(() => {
      const input = this.otpDigitInputs?.get(index)?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  private clearResendTimer(): void {
    if (!this.resendTimer) {
      return;
    }
    clearInterval(this.resendTimer);
    this.resendTimer = null;
  }
}
